<template>
  <!-- 帮助视频默认展示；离开帮助视频或关闭时卸载播放器，避免后台继续播放。 -->
  <a-modal :visible="visible" :title="title + ' · 帮助'" :footer="null" :width="860" :destroyOnClose="true" @cancel="$emit('close')">
    <a-tabs v-if="visible" v-model:activeKey="activeTab">
      <a-tab-pane key="video" tab="帮助视频">
        <div v-if="activeTab === 'video' && safeUrl" class="function-help-video">
          <video :key="safeUrl" :src="safeUrl" controls playsinline preload="metadata" @error="failed = true" @loadeddata="failed = false"></video>
          <p v-if="failed" role="alert">视频暂时无法播放，请检查网络或联系管理员核对视频地址。</p>
        </div>
        <a-empty v-else-if="activeTab === 'video'" description="暂无帮助视频，请查看使用说明。" />
      </a-tab-pane>
      <a-tab-pane key="text" tab="使用说明">
        <section class="function-help-text" aria-label="使用说明">
          <h3>适合做什么？</h3>
          <p>{{ description || '查看本页信息，处理当前账号可使用的管理功能。' }}</p>
          <h3>使用方法</h3>
          <div v-for="(section, index) in usageSteps" :key="section.title" class="function-help-step">
            <span aria-hidden="true">{{ index + 1 }}</span>
            <div><h4>{{ section.title }}</h4><p v-if="section.steps?.length === 1">{{ section.steps?.[0] }}</p><ol v-else><li v-for="step in section.steps" :key="step">{{ step }}</li></ol></div>
          </div>
          <template v-for="section in usageNotes" :key="section.title">
            <h3>{{ section.title }}</h3><p>{{ section.content }}</p>
          </template>
          <p class="function-help-note">请以当前页面实际显示的按钮、账号权限和处理结果为准。提交或保存后，查看页面反馈，并在对应列表、明细或设置中重新核对结果。</p>
        </section>
      </a-tab-pane>
      <a-tab-pane key="contact" tab="联系客服">
        <section class="function-help-contact" aria-label="联系客服">
          <a-spin :spinning="contactLoading">
            <div v-if="contactError" class="function-help-contact-error">
              <a-alert type="error" :message="contactError" show-icon />
              <a-button :loading="contactLoading" @click="loadContact">重新读取客服信息</a-button>
            </div>
            <template v-else-if="contactLoaded">
              <h3>管理端客服</h3>
              <p>平台与分店管理端共用此客服入口。</p>
              <div v-if="safeContactUrl" class="function-help-contact-options">
                <div class="function-help-contact-qr">
                  <qrcode-vue :value="safeContactUrl" :width="176" :margin="2" :color="{ dark: '#244568', light: '#ffffff' }" type="image/png" aria-label="微信扫码联系客服二维码" />
                  <span>手机微信扫码，直接进入客服对话</span>
                </div>
                <div class="function-help-contact-desktop">
                  <p>当前电脑已登录微信？点击下方按钮尝试打开客服对话。</p>
                  <a class="function-help-contact-link" :href="safeContactUrl" target="_blank" rel="noopener noreferrer">在当前电脑打开客服</a>
                  <p class="function-help-contact-hint">是否直接唤起微信由微信客户端和浏览器决定；若出现扫码页，请扫描左侧二维码。</p>
                </div>
              </div>
              <a-empty v-else description="平台暂未配置管理端客服，请稍后再试。" />
            </template>
          </a-spin>
        </section>
      </a-tab-pane>
      <a-tab-pane v-if="canManage" key="settings" tab="客服设置">
        <section class="function-help-contact" aria-label="管理端客服设置">
          <a-alert v-if="permissionLoading" type="info" message="正在核对当前账号的客服设置权限…" show-icon class="function-help-alert" />
          <a-alert v-else-if="permissionError" type="warning" :message="permissionError" show-icon class="function-help-alert" />
          <template v-if="canManage">
          <a-alert v-if="permissionError" type="info" message="已通过客服配置接口确认当前账号可维护；独立权限检查可稍后重试。" show-icon class="function-help-alert" />
          <p>平台统一设置，保存后平台与分店管理端的“联系客服”使用同一入口。两项都留空可暂停入口。</p>
          <a-alert v-if="settingsError" type="error" :message="settingsError" show-icon class="function-help-alert" />
          <a-alert v-if="settingsSaved" type="success" message="客服设置已保存，下方为服务器回读结果。" show-icon class="function-help-alert" />
          <a-spin :spinning="contactLoading">
            <a-form layout="vertical">
              <a-form-item label="企业微信客服链接">
                <a-input v-model:value="draftUrl" placeholder="https://work.weixin.qq.com/kfid/..." :disabled="settingsSaving || !contactLoaded || contactLoading || permissionLoading" />
              </a-form-item>
              <a-form-item label="企业ID（CorpID）">
                <a-input v-model:value="draftCorpId" placeholder="ww 开头的企业ID" :disabled="settingsSaving" />
              </a-form-item>
            </a-form>
            <div class="function-help-actions">
              <a-button :disabled="contactLoading || settingsSaving" @click="loadContact">重新读取</a-button>
              <a-button type="primary" :loading="settingsSaving" :disabled="!contactLoaded || contactLoading || settingsSaving || permissionLoading" @click="saveContact">保存客服设置</a-button>
            </div>
          </a-spin>
          </template>
          <div v-else-if="!permissionLoading">
            <a-alert v-if="!permissionError" type="info" :message="platformAccount ? '当前角色没有管理端客服设置权限。' : '管理端客服由平台统一配置，分店账号可以使用联系客服。'" show-icon class="function-help-alert" />
            <p v-if="platformAccount && !permissionError">请由有权限的平台管理员进入“角色权限”，为当前角色勾选系统管理下的“管理端客服设置”，保存后点击“重新检查”。它与来图制版订单中的客服设置不是同一项权限。</p>
            <p v-if="platformAccount && !permissionError">如果权限列表中也没有这一项，请平台管理员检查系统更新和权限初始化是否完成。</p>
            <p v-if="permissionError">请重新检查；仍失败时，请平台管理员核对当前管理端对应的后端服务和日志。</p>
            <a-button :loading="permissionLoading || contactLoading" @click="loadContact">重新检查</a-button>
          </div>
        </section>
      </a-tab-pane>
    </a-tabs>
  </a-modal>
</template>
<script lang="ts">
// 使用说明以文本渲染，视频沿用已有安全地址校验。
import { computed, defineComponent, onBeforeUnmount, ref, watch } from 'vue'
import QrcodeVue from 'vue-qrcode'
import store from '@/store'
import { readAdminHelpContact, readAdminHelpContactPermission, saveAdminHelpContact } from '@/api/adminHelpContact'
import { safeFunctionVideoUrl } from '@/utils/functionGuides'
import { resolveAdminFunctionUsage } from '@/utils/adminFunctionUsage'
export default defineComponent({
  components: { QrcodeVue },
  props: {
    visible: Boolean, url: { type: String, default: '' }, title: { type: String, default: '' },
    description: { type: String, default: '' }, path: { type: String, default: '' }
  },
  emits: ['close'],
  setup(props) {
    const activeTab = ref('video'), failed = ref(false)
    const safeUrl = computed(() => safeFunctionVideoUrl(props.url))
    const usageSections = computed(() => resolveAdminFunctionUsage(props.path))
    const usageSteps = computed(() => usageSections.value.filter(section => section.steps?.length))
    const usageNotes = computed(() => usageSections.value.filter(section => section.content))
    const contactLoaded=ref(false),contactLoading=ref(false),contactError=ref('')
    const permissionLoading=ref(false),permissionError=ref(''),permissionChecked=ref(false)
    const platformAccount=computed(()=>Number(store.getters.userInfo?.orgId)===1000)
    let overviewManage:boolean|undefined
    const canManage=ref(false),contactUrl=ref(''),draftUrl=ref(''),draftCorpId=ref('')
    const settingsSaving=ref(false),settingsError=ref(''),settingsSaved=ref(false)
    const identity=computed(()=>JSON.stringify([store.getters.token,store.getters.userInfo?.id,store.getters.userInfo?.orgId,store.getters.userInfo?.roleId]))
    const safeContactUrl=computed(()=>/^https:\/\/work\.weixin\.qq\.com\/kfid\/[A-Za-z0-9]{1,100}$/.test(contactUrl.value)?contactUrl.value:'')
    let generation=0,permissionGeneration=0
    function resetContact(){generation++;permissionGeneration++;contactLoaded.value=false;contactLoading.value=false;contactError.value='';canManage.value=false;permissionLoading.value=false;permissionError.value='';permissionChecked.value=false;overviewManage=undefined;contactUrl.value='';draftUrl.value='';draftCorpId.value='';settingsSaving.value=false;settingsError.value='';settingsSaved.value=false}
    async function loadPermission(){
      const version=++permissionGeneration,session=identity.value
      if(!props.visible)return
      permissionLoading.value=true;permissionError.value='';permissionChecked.value=false;canManage.value=false
      try{
        const response=await readAdminHelpContactPermission()
        if(!props.visible || version!==permissionGeneration || session!==identity.value)return
        if(typeof response.data!=='boolean')throw new Error('权限接口返回异常')
        permissionChecked.value=true;canManage.value=response.data
      }catch(error:any){
        if(props.visible && version===permissionGeneration && session===identity.value){
          const accessDenied=[401,403,8,40901].includes(Number(error?.code)) || [401,403].includes(error?.response?.status)
          permissionError.value=error?.response?.status===404?'当前服务缺少客服权限检查接口，请平台管理员核对后端更新。'
            :accessDenied?'客服权限检查被服务拒绝，请平台管理员核对登录状态和账号授权。'
            :'客服设置权限读取失败，请点击重新检查；这不代表当前角色没有权限。'
          permissionChecked.value=accessDenied
          canManage.value=!permissionChecked.value && overviewManage===true
        }
      }finally{if(props.visible && version===permissionGeneration && session===identity.value)permissionLoading.value=false}
    }
    function current(version:number,session:string){return props.visible && version===generation && session===identity.value}
    async function loadContact(){
      if(!props.visible || contactLoading.value || settingsSaving.value)return
      const version=++generation,session=identity.value
      contactLoading.value=true;contactLoaded.value=false;contactError.value='';settingsError.value='';settingsSaved.value=false
      overviewManage=undefined
      void loadPermission()
      try{
        const response=await readAdminHelpContact()
        if(!current(version,session))return
        const value=response.data
        if(typeof value?.customerServiceUrl!=='string' || typeof value?.customerServiceCorpId!=='string')throw new Error('客服信息返回异常')
        contactUrl.value=value.customerServiceUrl.trim();draftUrl.value=contactUrl.value;draftCorpId.value=value.customerServiceCorpId.trim()
        overviewManage=typeof value.canManage==='boolean'?value.canManage:undefined
        if(!permissionLoading.value && !permissionChecked.value)canManage.value=overviewManage===true
        contactLoaded.value=true
        contactError.value=typeof value.configurationError==='string'?value.configurationError:''
        settingsError.value=contactError.value
      }catch(error:any){
        if(current(version,session)){
          const detail=typeof error?.response?.data?.message==='string'?error.response.data.message:typeof error?.message==='string'?error.message.replace(/^Bad Request\s*/,'').trim():''
          contactError.value=error?.response?.status===404?'当前服务尚未提供管理端客服接口，请联系平台管理员更新测试或正式环境的后端服务。'
            : error?.response?.status>=500?'客服服务读取失败，请平台管理员检查客服配置和服务日志，修复后重新读取。'
            : error?.response?.status===403?'当前账号无权读取客服信息，请联系平台管理员核对角色权限。'
            : detail && /客服|权限|登录|配置/.test(detail)?detail:'客服信息暂时无法读取，请检查网络；重试仍失败请联系平台管理员核对客服配置。'
          settingsError.value=contactError.value
        }
      }
      finally{if(current(version,session))contactLoading.value=false}
    }
    async function saveContact(){
      if(!props.visible || !canManage.value || permissionLoading.value || !contactLoaded.value || contactLoading.value || settingsSaving.value)return
      const customerServiceUrl=draftUrl.value.trim(),customerServiceCorpId=draftCorpId.value.trim()
      if(Boolean(customerServiceUrl)!==Boolean(customerServiceCorpId)){settingsError.value='请同时填写客服链接和企业ID，或同时清空';return}
      if(customerServiceUrl && !/^https:\/\/work\.weixin\.qq\.com\/kfid\/[A-Za-z0-9]{1,100}$/.test(customerServiceUrl)){settingsError.value='请填写有效的企业微信客服链接';return}
      if(customerServiceCorpId && !/^ww[A-Za-z0-9]{8,64}$/.test(customerServiceCorpId)){settingsError.value='企业ID须以 ww 开头，后接 8 至 64 位字母或数字';return}
      const version=++generation,session=identity.value
      settingsSaving.value=true;settingsError.value='';settingsSaved.value=false
      try{
        const response=await saveAdminHelpContact({customerServiceUrl,customerServiceCorpId})
        if(!current(version,session))return
        const saved=response.data
        if(typeof saved?.customerServiceUrl!=='string' || typeof saved?.customerServiceCorpId!=='string' || saved.canManage!==true)throw new Error('保存回读异常')
        contactUrl.value=saved.customerServiceUrl;draftUrl.value=saved.customerServiceUrl;draftCorpId.value=saved.customerServiceCorpId;contactError.value='';settingsSaved.value=true
       }catch(error:any){if(current(version,session)){
        const detail=typeof error?.response?.data?.message==='string'?error.response.data.message:typeof error?.message==='string'?error.message:''
        settingsError.value=detail && /客服|权限|配置|初始化|登录/.test(detail)?detail+'；输入内容已保留':'保存失败，请检查权限或网络；输入内容已保留'
      }}
      finally{if(current(version,session))settingsSaving.value=false}
    }
    watch(() => props.visible, visible => { activeTab.value='video'; failed.value=false;resetContact();if(visible)void loadContact() }, { flush: 'sync', immediate:true })
    watch(identity,()=>{resetContact();if(props.visible)void loadContact()},{flush:'sync'})
    // 撤权或重新核验时移出已隐藏的设置页，避免保留旧表单。
    watch(canManage,allowed=>{if(!allowed && activeTab.value==='settings')activeTab.value='contact'},{flush:'sync'})
    watch(activeTab,tab=>{if((tab==='contact'||tab==='settings') && props.visible && !contactLoaded.value && !contactLoading.value)void loadContact()})
    watch(() => [props.url, activeTab.value], () => { failed.value = false }, { flush: 'sync' })
    onBeforeUnmount(resetContact)
    return { activeTab, failed, safeUrl, usageSteps, usageNotes, contactLoaded, contactLoading, contactError,
      canManage, permissionLoading, permissionError, platformAccount, safeContactUrl, draftUrl, draftCorpId, settingsSaving, settingsError, settingsSaved, loadContact, saveContact }
  }
})
</script>
<style scoped>
/* 和报表设计说明共用用途、编号步骤、注意事项的阅读层次。 */
.function-help-text { min-height:180px; max-height:60vh; overflow:auto; padding:8px 12px 20px; color:#6d829a; line-height:1.9; }
.function-help-text h3 { margin:22px 0 9px; color:#244568; font-size:16px; }
.function-help-text h3:first-of-type { margin-top:0; }
.function-help-step { display:flex; gap:15px; margin:24px 0; }
.function-help-step>span { flex:none; width:32px; height:32px; border-radius:9px; background:#e9f4ff; color:#1890ff; font-weight:600; line-height:32px; text-align:center; }
.function-help-step>div { min-width:0; }
.function-help-step h4 { margin:0 0 6px; color:#325375; font-size:14px; font-weight:600; }
.function-help-step p { font-size:13px; }
.function-help-text p,.function-help-text li { white-space:pre-wrap; overflow-wrap:anywhere; line-height:1.8; }
.function-help-text p { margin:0; }
.function-help-text ol { margin:0; padding-left:19px; list-style:decimal; font-size:13px; }
.function-help-text li + li { margin-top:6px; }
.function-help-text .function-help-note { margin-top:24px; padding:15px; border-radius:7px; background:#f1f7fc; font-size:12px; }
.function-help-video video { display:block; width:100%; max-height:60vh; background:#000; }
.function-help-video p { color:#cf1322; margin:12px 0 0; }
.function-help-contact { min-height:180px; max-height:60vh; overflow:auto; padding:14px 12px 20px; color:#526d88; }
.function-help-contact h3 { color:#244568; font-size:16px; }
.function-help-contact p { line-height:1.8; }
.function-help-contact-error .ant-btn { margin-top:14px; }
.function-help-contact-options { display:flex; align-items:center; gap:28px; flex-wrap:wrap; }
.function-help-contact-qr { display:flex; flex-direction:column; align-items:center; gap:9px; width:210px; padding:14px; background:#f4f9ff; border:1px solid #dcecff; border-radius:10px; text-align:center; font-size:12px; }
.function-help-contact-qr img { display:block; max-width:100%; }
.function-help-contact-desktop { flex:1; min-width:220px; }
.function-help-contact-hint { color:#7890a7; font-size:12px; }
.function-help-contact-link { display:inline-flex; margin:16px 0; padding:8px 18px; border-radius:18px; background:#1890ff; color:#fff; font-weight:600; }
.function-help-contact-link:hover { color:#fff; background:#096dd9; }
.function-help-alert { margin:12px 0; }
.function-help-actions { display:flex; justify-content:flex-end; gap:10px; }
</style>
